import type { TipTapDoc } from "@compass/shared/schema/tiptapDoc";
import { useSnackbar } from "notistack";
import * as React from "react";
import { useMemo, useState } from "react";
import { CommentReplies } from "components/common/CommentReplies";
import { UnifiedThreadComment } from "components/common/UnifiedThreadComment";
import { Spinner } from "components/elements";
import { ThreadCommentForm } from "components/form/ThreadCommentForm";
import { useCurrentUser } from "hooks/useCurrentUser";
import type { ApiThreadComment } from "lib/threadCommentUtils";
import { trpc } from "lib/trpc";
import { ClientPost } from "@compass/shared/types";

type SocialCommentsProps = {
    post: ClientPost;
    threadId: number;
    limit?: number;
};

export const SocialCommentsWrapper = ({ post, threadId, limit }: SocialCommentsProps) => {
    const queryClient = trpc.useContext();
    const { enqueueSnackbar } = useSnackbar();
    const currentUser = useCurrentUser();
    const [editingComment, setEditingComment] = useState<ApiThreadComment | null>(null);
    const [replyingToComment, setReplyingToComment] = useState<ApiThreadComment | null>(null);
    const [expandedCommentIds, setExpandedCommentIds] = useState<Set<number>>(new Set());

    // Fetch thread comments
    const { data: threadComments = [] } = trpc.threads.getComments.useQuery(
        { threadId },
        {
            refetchOnMount: true,
            refetchOnWindowFocus: true,
            staleTime: 60_000,
            refetchOnReconnect: true,
        },
    );

    const sortedComments = useMemo(() => {
        const sorted = threadComments
            .slice()
            .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        // Skip the first comment — it's the post body content, not a reply
        const replies = sorted.length > 0 ? sorted.slice(1) : sorted;
        return limit ? replies.slice(-limit) : replies;
    }, [threadComments, limit]);

    const deleteCommentMutation = trpc.threads.deleteComment.useMutation();
    const updateCommentMutate = trpc.threads.updateComment.useMutation();
    const createReplyMutation = trpc.threads.createComment.useMutation();

    const handleUpdateComment = (
        commentId: number,
        contentJson: TipTapDoc,
        isPostedAnonymously: boolean,
        attachmentIds: string[],
        callback: () => void,
    ) => {
        updateCommentMutate.mutate(
            {
                commentId,
                contentJson,
                isPostedAnonymously,
                attachmentIds,
            },
            {
                onSuccess: async () => {
                    callback();
                    await queryClient.threads.getComments.invalidate();
                    queryClient.threads.getChildComments.invalidate();
                    queryClient.social.posts.get.invalidate({ postId: post.id });
                    queryClient.social.posts.latest.invalidate();
                    queryClient.feed.unified.invalidate();
                    enqueueSnackbar("Comment updated successfully.", { variant: "success" });
                },
                onError: (e) => {
                    console.error(e);
                    enqueueSnackbar(`${e}`, { variant: "error" });
                },
            },
        );
    };

    const handleDeleteComment = (commentId: number) => {
        if (deleteCommentMutation.isPending) return;
        deleteCommentMutation.mutate(
            { commentId },
            {
                onSuccess: async () => {
                    await queryClient.threads.getComments.invalidate();
                    queryClient.threads.getChildComments.invalidate();
                    queryClient.social.posts.get.invalidate({ postId: post.id });
                    queryClient.social.posts.latest.invalidate();
                    queryClient.feed.unified.invalidate();
                    queryClient.notifications.latest.invalidate();
                    enqueueSnackbar("Comment deleted successfully", { variant: "success" });
                },
            },
        );
    };

    return (
        <>
            {deleteCommentMutation.isPending && (
                <div className="z-100 absolute left-0 top-0 flex h-full w-full items-center justify-center bg-slate-50/50">
                    <Spinner className="h-10 w-10 text-slate-600" />
                </div>
            )}
            <div className="flex flex-col gap-4">
                {sortedComments.map((comment) => (
                    <UnifiedThreadComment
                        key={comment.id}
                        comment={comment}
                        context="social"
                        currentUser={currentUser}
                        onEdit={() => setEditingComment(comment)}
                        onDelete={() => handleDeleteComment(comment.id)}
                        onReply={() => setReplyingToComment(comment)}
                        metadataSlot={
                            <div className="mt-2">
                                {/* TODO: Add SocialCommentMeta for reactions */}
                            </div>
                        }
                        repliesSlot={
                            <CommentReplies
                                comment={comment}
                                context="social"
                                threadId={threadId}
                                currentUser={currentUser}
                                onReply={(c) => setReplyingToComment(c)}
                                onEdit={(c) => setEditingComment(c)}
                                onDelete={handleDeleteComment}
                                forceExpanded={expandedCommentIds.has(comment.id)}
                            />
                        }
                    />
                ))}
            </div>
            {threadComments.length ? <div className="h-4" /> : null}

            {editingComment && (
                <div className="fixed inset-0 z-50 bg-[#424242]/75">
                    <div className="fixed left-1/2 top-1/2 z-50 w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-4">
                        <ThreadCommentForm
                            context="social"
                            mode="edit"
                            initialContentJson={editingComment.contentJson}
                            initialAttachments={editingComment.attachments}
                            initialIsAnonymous={editingComment.isPostedAnonymously}
                            onSubmit={(data) => {
                                handleUpdateComment(
                                    editingComment.id,
                                    data.contentJson,
                                    data.isAnonymous,
                                    data.attachmentIds,
                                    () => {
                                        setEditingComment(null);
                                    },
                                );
                            }}
                            onCancel={() => setEditingComment(null)}
                            showCancel
                            isLoading={updateCommentMutate.isPending}
                            submitLabel="Update Comment"
                            showUserIdentity
                        />
                    </div>
                </div>
            )}

            {replyingToComment && (
                <div className="fixed inset-0 z-50 bg-[#424242]/75">
                    <div className="fixed left-1/2 top-1/2 z-50 w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-4">
                        <ThreadCommentForm
                            context="social"
                            replyingToComment={replyingToComment}
                            onSubmit={(data) => {
                                createReplyMutation.mutate(
                                    {
                                        threadId,
                                        contentJson: data.contentJson,
                                        parentCommentId: replyingToComment.id,
                                        isPostedAnonymously: data.isAnonymous,
                                        attachmentIds: data.attachmentIds,
                                    },
                                    {
                                        onSuccess: async () => {
                                            setExpandedCommentIds((prev) =>
                                                new Set(prev).add(replyingToComment.id),
                                            );
                                            setReplyingToComment(null);
                                            await queryClient.threads.getComments.invalidate();
                                            queryClient.threads.getChildComments.invalidate();
                                            queryClient.social.posts.get.invalidate({
                                                postId: post.id,
                                            });
                                            queryClient.social.posts.latest.invalidate();
                                            queryClient.feed.unified.invalidate();
                                            enqueueSnackbar("Reply added successfully.", {
                                                variant: "success",
                                            });
                                        },
                                        onError: (e) => {
                                            enqueueSnackbar(
                                                e instanceof Error ? e.message : String(e),
                                                {
                                                    variant: "error",
                                                },
                                            );
                                        },
                                    },
                                );
                            }}
                            onCancel={() => setReplyingToComment(null)}
                            showCancel
                            isLoading={createReplyMutation.isPending}
                            submitLabel="Reply"
                            showUserIdentity
                        />
                    </div>
                </div>
            )}
        </>
    );
};
